// Order is load-bearing: this is the low→high ramp the composer uses for bar
// count and cycle order, not just a validation set — don't reorder.
export const effortLevels = ["low", "medium", "high", "max"] as const;

export type EffortLevel = (typeof effortLevels)[number];

export function isEffortLevel(value: unknown): value is EffortLevel {
  return (
    typeof value === "string" &&
    (effortLevels as readonly string[]).includes(value)
  );
}

// Per-model effort capability, declared on each manifest — the single source of
// truth so the composer, AI settings, and the per-request mapping all agree on
// what a given model supports. `modelEfforts` is an ordered rule list matched
// against the model id; the first matching rule's `levels` win, and a model that
// matches no rule (or a provider with no rules) has no reasoning-effort knob.
// `defaultChatEffort` is the default level, pre-selected and clamped to whatever
// the active model actually offers.
export type ModelEffortRule = {
  pattern: RegExp;
  levels: readonly EffortLevel[];
};

export type EffortCapability = {
  defaultChatEffort?: EffortLevel;
  modelEfforts?: readonly ModelEffortRule[];
};

export function availableEffortLevels(
  capability: EffortCapability,
  model: string
): readonly EffortLevel[] {
  if (!capability.modelEfforts) return [];
  return (
    capability.modelEfforts.find((rule) => rule.pattern.test(model))?.levels ??
    []
  );
}

// Clamp a requested level to what the model offers, or null when the model has
// no reasoning-effort knob (a non-reasoning model, or a `max` default left over
// from switching off a top-tier model).
export function clampEffort(
  capability: EffortCapability,
  model: string,
  effort: EffortLevel
): EffortLevel | null {
  const available = availableEffortLevels(capability, model);
  if (available.includes(effort)) return effort;
  return available.at(-1) ?? null;
}
